import { PriorAuthGateOutcomeT } from '@lotushealth/lotushealth_js_sdk/schemas';
import { Loader2, ShieldAlert } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Checkbox } from '@/shared/ui/shadcn/checkbox';
import { Label } from '@/shared/ui/shadcn/label';
import { cn } from '@/lib/utils';

export interface PriorAuthBannerProps {
  outcome: PriorAuthGateOutcomeT | null;
  isLoading: boolean;
  medicationDisplayName: string;
  /** Controlled expedited flag — shown when PA will be created (PA_REQUIRED or EXCLUDED). */
  expedited?: boolean;
  /** Called when the clinician toggles the expedited checkbox. */
  onExpeditedChange?: (checked: boolean) => void;
}

/**
 * Soft pre-submit "heads up" for prior-auth posture.
 *
 * We only render when the gate comes back with ``PA_REQUIRED`` or
 * ``EXCLUDED`` — the other three outcomes (``NO_COVERAGE``, ``NO_PA``,
 * ``UNKNOWN``) are not actionable for the clinician and would add
 * noise to the review step. In particular, ``UNKNOWN`` usually means
 * "we couldn't reach DoseSpot for a formulary lookup" which, per the
 * approved copy, should NOT block or warn — the signing path runs the
 * same gate for real and will surface any issues then.
 *
 * The copy is deliberately tentative ("Likely prior authorization"…
 * "Final determination happens after signing.") because the preview
 * runs the formulary check but does NOT hit live eligibility. A drug
 * marked ``PA_REQUIRED`` here will almost always still be PA-required
 * at sign time, but the absolute truth lives in the sign-time gate.
 *
 * When ``PA_REQUIRED``, the expedited priority checkbox is shown per
 * SureScripts ACR PA.132.1 — allows the clinician to indicate that
 * the PA request should be processed with expedited priority.
 */
export function PriorAuthBanner({
  outcome,
  isLoading,
  medicationDisplayName,
  expedited,
  onExpeditedChange,
}: PriorAuthBannerProps) {
  if (isLoading && outcome === null) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-border/40 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" aria-hidden />
        Checking prior authorization status…
      </div>
    );
  }
  if (outcome !== PriorAuthGateOutcomeT.PA_REQUIRED && outcome !== PriorAuthGateOutcomeT.EXCLUDED) {
    return null;
  }

  const isExcluded = outcome === PriorAuthGateOutcomeT.EXCLUDED;
  const isPaRequired = outcome === PriorAuthGateOutcomeT.PA_REQUIRED;
  const title = isExcluded
    ? `${medicationDisplayName} may be excluded from coverage`
    : 'Likely prior authorization';
  const description = isExcluded
    ? `Based on payer formulary, ${medicationDisplayName} appears to be excluded from the patient's plan. Final determination happens after signing.`
    : 'Based on payer formulary. Final determination happens after signing.';

  return (
    <Alert
      className={cn(
        'border-amber-400/40 bg-amber-50 text-amber-900',
        'dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-100'
      )}
      aria-live="polite"
    >
      <ShieldAlert className="text-amber-700 dark:text-amber-200" aria-hidden />
      <AlertTitle className="break-words">{title}</AlertTitle>
      <AlertDescription className="text-amber-900/90 dark:text-amber-100/90">
        {description}
      </AlertDescription>
      {(isPaRequired || isExcluded) && onExpeditedChange ? (
        <div className="mt-3 flex items-center gap-2">
          <Checkbox
            id="pa-expedited"
            checked={expedited ?? false}
            onCheckedChange={(checked) => onExpeditedChange(checked === true)}
          />
          <Label
            htmlFor="pa-expedited"
            className="cursor-pointer text-xs font-medium text-amber-900 dark:text-amber-100"
          >
            Request expedited review
          </Label>
        </div>
      ) : null}
    </Alert>
  );
}
